import React, { useEffect, useMemo, useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import moment from 'moment'

import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import RootState from '../../core/root-state'
import {
  itemPriceHistoryActions,
  ItemPriceHistory,
  ItemPriceHistoryParam,
  ItemPriceHistoryPagination,
} from '../../core/item-price-history'
import { itemsActions, Item } from '../../core/items'
import {
  Brand,
  brandsActions,
  getDefaultBrandCodeByRole,
  BrandCode,
} from '../../core/brands'
import { roleType, Permission, isSbjUser } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import ItemPriceHistoryFiltersComponent, {
  ItemPriceHistoryFilterForm,
} from '../components/item-price-history/item-price-history-filters'
import ItemPriceHistoryTable from '../components/item-price-history/item-price-history-table'
import Pagination from '../components/pagination/pagination'
import styles from '../styles/modules/item-price-history/item-price-history.module.scss'

interface StateProps {
  histories: ItemPriceHistory[]
  pagination: ItemPriceHistoryPagination
  isLoading: boolean
  items: Item[]
  brands: Brand[]
  role: roleType
}

interface DispatchProps {
  fetchItemPriceHistories: (params?: Partial<ItemPriceHistoryParam>) => void
  fetchItems: (brandCode?: string | null) => void
  fetchBrands: () => void
}

type Props = StateProps & DispatchProps

const formatDate = (value: Date | null) =>
  value ? moment(value).format('YYYY-MM-DD') : null

const ItemPriceHistoryScreen = ({
  histories,
  pagination,
  isLoading,
  items,
  brands,
  role,
  fetchItemPriceHistories,
  fetchItems,
  fetchBrands,
}: Props) => {
  const isAdmin = role === roleType.ADMIN
  const defaultBrandCode = useMemo(
    () => getDefaultBrandCodeByRole(isSbjUser(role)) || '',
    [role]
  )

  const [formState, setFormState] = useState<ItemPriceHistoryFilterForm>({
    itemId: '',
    dateFrom: null,
    dateTo: null,
    brandCode: defaultBrandCode || '',
  })

  useEffect(() => {
    if (isAdmin) {
      fetchBrands()
    }
  }, [isAdmin, fetchBrands])

  useEffect(() => {
    const initialBrand = isAdmin
      ? formState.brandCode || defaultBrandCode
      : defaultBrandCode

    fetchItems(initialBrand || null)
    fetchItemPriceHistories({
      brandCode: initialBrand || null,
      page: 1,
      per: 20,
    })
    setFormState(prev => ({
      ...prev,
      brandCode: initialBrand || '',
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (field: 'itemId' | 'brandCode', value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleItemChange = (itemId: string) => {
    setFormState(prev => ({
      ...prev,
      itemId,
    }))
    // 選択と同時にAPIリクエストを発行
    const params: Partial<ItemPriceHistoryParam> = {
      itemId: itemId ? Number(itemId) : null,
      dateFrom: formatDate(formState.dateFrom),
      dateTo: formatDate(formState.dateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchItemPriceHistories(params)
  }

  const handleBrandChange = (brandCode: BrandCode | '') => {
    fetchItems(brandCode || null)
    setFormState(prev => ({
      ...prev,
      itemId: '',
      brandCode,
    }))
    // ブランド変更時も即座にAPIリクエストを発行
    const params: Partial<ItemPriceHistoryParam> = {
      itemId: null,
      dateFrom: formatDate(formState.dateFrom),
      dateTo: formatDate(formState.dateTo),
      brandCode: brandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchItemPriceHistories(params)
  }

  const buildParams = (override?: Partial<ItemPriceHistoryParam>) => {
    const params: Partial<ItemPriceHistoryParam> = {
      itemId: formState.itemId ? Number(formState.itemId) : null,
      dateFrom: formatDate(formState.dateFrom),
      dateTo: formatDate(formState.dateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    return {
      ...params,
      ...override,
    }
  }

  const handleDateFromChange = (date: Date | null) => {
    if (!date) {
      setFormState(prev => ({
        ...prev,
        dateFrom: null,
      }))
      // 日付クリア時も即座にAPIリクエストを発行
      const params: Partial<ItemPriceHistoryParam> = {
        itemId: formState.itemId ? Number(formState.itemId) : null,
        dateFrom: null,
        dateTo: formatDate(formState.dateTo),
        brandCode: isAdmin
          ? formState.brandCode || null
          : defaultBrandCode || null,
        page: 1,
        per: pagination.per || 20,
      }
      fetchItemPriceHistories(params)
      return
    }

    const today = new Date()
    let nextStartDate = date > today ? today : date

    if (formState.dateTo && nextStartDate > formState.dateTo) {
      nextStartDate = formState.dateTo
    }

    setFormState(prev => ({
      ...prev,
      dateFrom: nextStartDate,
    }))

    // 日付変更時も即座にAPIリクエストを発行
    const params: Partial<ItemPriceHistoryParam> = {
      itemId: formState.itemId ? Number(formState.itemId) : null,
      dateFrom: formatDate(nextStartDate),
      dateTo: formatDate(formState.dateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchItemPriceHistories(params)
  }

  const handleDateToChange = (date: Date | null) => {
    let newDateTo: Date | null = null
    let newDateFrom: Date | null = formState.dateFrom

    if (date > formState.dateFrom) {
      const now = new Date()
      if (date < now) {
        newDateTo = date
      } else {
        newDateTo = now
      }
      setFormState(prev => ({
        ...prev,
        dateTo: newDateTo,
      }))
    } else {
      newDateFrom = date
      newDateTo = date
      setFormState(prev => ({
        ...prev,
        dateFrom: date,
        dateTo: date,
      }))
    }

    // 日付変更時も即座にAPIリクエストを発行
    const params: Partial<ItemPriceHistoryParam> = {
      itemId: formState.itemId ? Number(formState.itemId) : null,
      dateFrom: formatDate(newDateFrom),
      dateTo: formatDate(newDateTo),
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchItemPriceHistories(params)
  }

  const handleClear = () => {
    // フォームをクリア（ブランドは維持）
    setFormState({
      itemId: '',
      dateFrom: null,
      dateTo: null,
      brandCode: formState.brandCode,
    })
    // クリア後の初期データ取得
    const params: Partial<ItemPriceHistoryParam> = {
      itemId: null,
      dateFrom: null,
      dateTo: null,
      brandCode: isAdmin
        ? formState.brandCode || null
        : defaultBrandCode || null,
      page: 1,
      per: pagination.per || 20,
    }
    fetchItemPriceHistories(params)
  }

  const handlePageChange = (page: number) => {
    fetchItemPriceHistories(buildParams({ page }))
  }

  return (
    <Layout location={'item_price_history'}>
      <Wrapper title="価格変更履歴">
        <div className={styles.container}>
          <ItemPriceHistoryFiltersComponent
            formState={formState}
            onChange={handleChange}
            onItemChange={handleItemChange}
            onClear={handleClear}
            onBrandChange={handleBrandChange}
            onChangeDateFrom={handleDateFromChange}
            onChangeDateTo={handleDateToChange}
            items={items}
            brands={brands}
            isAdmin={isAdmin}
            isLoading={isLoading}
          />
          <ItemPriceHistoryTable histories={histories} isAdmin={isAdmin} />
          <div className={styles.pagination}>
            <Pagination
              currentPage={pagination.page || 1}
              totalCount={pagination.total}
              pageSize={pagination.per}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  histories: state.itemPriceHistory.histories,
  pagination: state.itemPriceHistory.pagination,
  isLoading: state.itemPriceHistory.isLoading,
  items: state.items.items,
  brands: state.brands.brands,
  role: state.auth0.role,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  fetchItemPriceHistories: (params?: ItemPriceHistoryParam) =>
    dispatch(itemPriceHistoryActions.fetchItemPriceHistories(params)),
  fetchItems: (brandCode?: string | null) =>
    dispatch(itemsActions.fetchItems(brandCode)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedItemPriceHistoryScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(ItemPriceHistoryScreen)

export default withPermissionCheck(ConnectedItemPriceHistoryScreen, {
  requiredPermission: Permission.ITEM_READ,
  redirectTo: '/404',
})
